import { useGetJobsLazyQuery } from "@graphql/generated";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import SearchableSelector, { Option } from "../SearchableSelector";

import { FunctionComponent, SVGProps } from "react";
import AiBrainIdeaIcon from "./icons/aiBrainIdea.svg?react";
import AiNeuralNetworksIcon from "./icons/aiNeuralNetworks.svg?react";
import ApacheNetBeansIcon from "./icons/apacheNetBeans.svg?react";
import AtmosphereIcon from "./icons/atmosphere.svg?react";
import BagIcon from "./icons/bag.svg?react";
import DiamondStarIcon from "./icons/diamondStar.svg?react";
import EarthIcon from "./icons/earth.svg?react";
import FullBrightnessIcon from "./icons/fullBrightness.svg?react";
import GemIcon from "./icons/gem.svg?react";
import GlobeIcon from "./icons/globe.svg?react";
import LayersIcon from "./icons/layers.svg?react";
import LightIcon from "./icons/light.svg?react";
import MachineIcon from "./icons/machine.svg?react";
import MultiChartIcon from "./icons/multiChart.svg?react";
import OpenUpIcon from "./icons/openUp.svg?react";
import PlanetIcon from "./icons/planet.svg?react";
import RocketSpaceIcon from "./icons/rocketSpace.svg?react";
import SettingIcon from "./icons/setting.svg?react";
import SpaceShuttleIcon from "./icons/spaceShuttle.svg?react";
import TestIcon from "./icons/test.svg?react";
import WorkerIcon from "./icons/worker.svg?react";

type IconType = FunctionComponent<SVGProps<SVGSVGElement> & { title?: string | undefined }>;

const jobIcons: IconType[] = [
  AtmosphereIcon,
  EarthIcon,
  GlobeIcon,
  AiBrainIdeaIcon,
  AiNeuralNetworksIcon,
  ApacheNetBeansIcon,
  BagIcon,
  DiamondStarIcon,
  FullBrightnessIcon,
  GemIcon,
  LayersIcon,
  LightIcon,
  MachineIcon,
  MultiChartIcon,
  OpenUpIcon,
  PlanetIcon,
  RocketSpaceIcon,
  SettingIcon,
  SpaceShuttleIcon,
  TestIcon,
  WorkerIcon,
];

interface JobInterest {
  value: string;
  label: string;
  description: string;
  icon: IconType;
}

interface JobInterestSelectorProps {
  selectedJobInterest?: string | string[];
  onSelect: (value: string | string[]) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const JobInterestSelector = ({ selectedJobInterest, onSelect, scrollContainerRef }: JobInterestSelectorProps) => {
  const [jobInterests, setJobInterests] = useState<JobInterest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const isLoadingMore = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedJobInterest) {
      if (Array.isArray(selectedJobInterest)) {
        setSelectedJobs(selectedJobInterest);
      } else {
        setSelectedJobs(selectedJobInterest ? [selectedJobInterest] : []);
      }
    }
  }, [selectedJobInterest]);

  const [fetchJobs, { loading, data }] = useGetJobsLazyQuery({
    variables: {
      first: 20,
      title_Icontains: searchTerm,
    },
    fetchPolicy: "cache-and-network",
  });

  const loadMoreJobs = useCallback(() => {
    if (data?.common?.jobs?.pageInfo.endCursor && !isLoadingMore.current) {
      isLoadingMore.current = true;
      fetchJobs({
        variables: {
          first: 20,
          title_Icontains: searchTerm,
          after: data.common.jobs.pageInfo.endCursor,
        },
      });
    }
  }, [data, fetchJobs, searchTerm]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreJobs || loading) return;

      const scrollContainer = scrollContainerRef?.current || containerRef.current;
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreJobs();
      }
    };

    const scrollContainer = scrollContainerRef?.current || containerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMoreJobs, loading, loadMoreJobs, scrollContainerRef]);

  useEffect(() => {
    if (data?.common?.jobs?.edges) {
      const newJobs = data.common.jobs.edges.map((edge) => {
        const randomIcon = jobIcons[Math.floor(Math.random() * jobIcons.length)];

        return {
          value: edge?.node?.id ?? "",
          label: edge?.node?.title ?? "",
          description: "",
          icon: randomIcon,
        } as JobInterest;
      });

      if (isLoadingMore.current) {
        setJobInterests((prevJobs) => {
          const existingIds = new Set(prevJobs.map((j) => j.value));
          const uniqueNewJobs = newJobs.filter((j) => !existingIds.has(j.value));
          return [...prevJobs, ...uniqueNewJobs];
        });
      } else {
        setJobInterests(newJobs);
      }

      setHasMoreJobs(!!data.common.jobs.pageInfo.hasNextPage);
      isLoadingMore.current = false;
    }
  }, [data]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    isLoadingMore.current = false;
    setJobInterests([]);
    fetchJobs({
      variables: {
        first: 20,
        title_Icontains: search,
        after: "",
      },
    });
  };

  const handleJobSelection = (value: string) => {
    setSelectedJobs((prev) => {
      const newSelection = prev.includes(value) ? prev.filter((id) => id !== value) : [...prev, value];

      onSelect(newSelection);
      return newSelection;
    });
  };

  const renderJobInterestItem = (jobInterest: Option & Partial<JobInterest>) => {
    const IconComponent = jobInterest.icon;

    return (
      <div className="flex flex-col justify-center items-center gap-2">
        <div className="w-8 h-8 mb-2 flex items-center justify-center">{IconComponent && <IconComponent />}</div>
        <p className="text-xs font-semibold mb-1">{jobInterest.label}</p>
        <p className="text-xs text-gray-600 line-clamp-3">{jobInterest.description}</p>
      </div>
    );
  };

  return (
    <div ref={containerRef}>
      <motion.h2
        className="text-xl lg:text-3xl font-bold mb-4 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        What job are you interested in?
      </motion.h2>
      <p className="text-gray-600 mb-4">Select one or more jobs that interest you</p>
      <div className="w-full mx-auto">
        <SearchableSelector
          options={jobInterests}
          selectedValue={selectedJobs}
          onSelect={handleJobSelection}
          placeholder="Search for a job interest..."
          loading={loading}
          onSearch={handleSearch}
          hasMore={hasMoreJobs}
          virtualized={false}
          renderOption={renderJobInterestItem}
          gridLayout
          containerScrolling
          multiSelect
        />
      </div>
    </div>
  );
};

export default JobInterestSelector;
